import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LEAGUE_SESSIONS } from '@league/shared/hq';
import { useEffect, useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPut } from '../../lib/api';

/**
 * THE DIALS.
 *
 * Two kinds of number, and they behave differently on purpose.
 *
 * THE TARGETS start empty and stay empty until she fills them in. They are business numbers — what
 * a good fill rate is, what share of sessions should be scored the same evening — and none of them
 * has an answer an engineer could work out. An empty target is not a gap in the form; it is the
 * dashboard promising not to call anything a failure on a number nobody chose.
 *
 * THE BOUNDS start with real values, because they describe what the platform can physically do:
 * twenty-eight is the largest league the seating engine and the print packs are built for. League
 * length is no longer a bound HQ keeps: any one to twenty-four sessions, chosen per league
 * (2026-10-19).
 *
 * Everything on this page is audited. What the dashboard calls a failure, and what a director is
 * allowed to configure, are things somebody will eventually ask who changed.
 */

interface Settings {
  targets: {
    activeLeagues: number | null;
    fillRate: number | null;
    playersOnTheBooks: number | null;
    returnRate: number | null;
    sameDayScoreEntry: number | null;
    kitOnTime: number | null;
  };
  bounds: {
    maxPlayerCap: number | null;
    minPlayerCap: number | null;
    rosterLockDays: number | null;
    /**
     * ⚠ THIS WAS MISSING AND `contract-drift.spec.ts` IS WHAT FOUND IT. The billing work added it
     * to `platform_settings` and to the API's projection; this interface was not updated in the
     * same commit, which is the exact failure mode that guard exists for — and the exact one that
     * once made a `registered` enrollment render nowhere.
     *
     * It also had to become a FIELD rather than a line on the interface, because the schema's own
     * comment says why it is a setting at all: "the commissioner will want to raise it, and that
     * must not be a deploy." A number she cannot see is a number she cannot raise.
     */
    foundingDirectorCap: number | null;
    /** How long a player promoted from the waitlist has to pay. Hers to set, for every league. */
    waitlistPaymentHours: number | null;
  };
}

type TargetKey = keyof Settings['targets'];

const TARGETS: { key: TargetKey; label: string; unit: 'count' | 'share'; what: string }[] = [
  {
    key: 'activeLeagues',
    label: 'Active leagues',
    unit: 'count',
    what: 'How many you want playing at once.',
  },
  {
    key: 'fillRate',
    label: 'Fill rate',
    unit: 'share',
    what: 'Share of seats taken across everything in flight.',
  },
  {
    key: 'playersOnTheBooks',
    label: 'Players on the books',
    unit: 'count',
    what: 'People holding a seat somewhere.',
  },
  {
    key: 'returnRate',
    label: 'Return rate',
    unit: 'share',
    what: 'Of the people who finish a league, how many join another.',
  },
  {
    key: 'sameDayScoreEntry',
    label: 'Same-session scores',
    unit: 'share',
    what: 'Sessions written down before the director goes to bed.',
  },
  {
    key: 'kitOnTime',
    label: 'Kit on time',
    unit: 'share',
    what: 'Packs confirmed arrived before session one.',
  },
];

/** A share is stored 0–1 and typed as a percentage, because nobody thinks in 0.85. */
function toField(value: number | null, unit: 'count' | 'share'): string {
  if (value === null) return '';
  return unit === 'share' ? String(Math.round(value * 100)) : String(value);
}

function fromField(text: string, unit: 'count' | 'share'): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return unit === 'share' ? parsed / 100 : Math.round(parsed);
}

export function HqSettings() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [bounds, setBounds] = useState<Settings['bounds'] | null>(null);
  const [saved, setSaved] = useState(false);

  const settings = useQuery({
    queryKey: ['hq', 'settings'],
    queryFn: () => apiFetch<{ settings: Settings }>('/api/hq/settings'),
  });

  useEffect(() => {
    const loaded = settings.data?.settings;
    if (!loaded) return;
    setDraft(
      Object.fromEntries(
        TARGETS.map((target) => [target.key, toField(loaded.targets[target.key], target.unit)]),
      ),
    );
    setBounds(loaded.bounds);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (body: Settings) => apiPut<{ settings: Settings }>('/api/hq/settings', body),
    onSuccess: async () => {
      setSaved(true);
      /* The dashboard reads these, so it has to be re-asked or it keeps showing yesterday's verdicts. */
      await queryClient.invalidateQueries({ queryKey: ['hq'] });
    },
  });

  if (settings.isPending || !bounds) {
    return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;
  }

  const error = save.error instanceof ApiError ? save.error.message : null;

  const submit = () => {
    setSaved(false);
    save.mutate({
      targets: Object.fromEntries(
        TARGETS.map((target) => [target.key, fromField(draft[target.key] ?? '', target.unit)]),
      ) as Settings['targets'],
      bounds,
    });
  };

  return (
    <div className="flex max-w-[52rem] flex-col gap-8 px-6 py-8">
      <header>
        <h1 className="font-display text-3xl leading-tight">Settings</h1>
        <p className="mt-1 text-[14px] text-ink-2">
          What the platform holds itself to, and what a director may configure.
        </p>
      </header>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          What good looks like
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
          Leave one blank and the dashboard simply shows the number. A tile only turns rust when it
          misses a target you set here &mdash; nothing on this platform invents a benchmark to fail
          against.
        </p>

        <div className="mt-4 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {TARGETS.map((target) => (
            <label
              key={target.key}
              className="flex flex-wrap items-center justify-between gap-4 px-5 py-3.5"
            >
              <span className="min-w-0">
                <span className="block text-[14px] font-medium">{target.label}</span>
                <span className="block text-[13px] text-ink-2">{target.what}</span>
              </span>
              <span className="flex items-center gap-2">
                <input
                  inputMode="numeric"
                  value={draft[target.key] ?? ''}
                  onChange={(event) => {
                    setSaved(false);
                    setDraft((current) => ({ ...current, [target.key]: event.target.value }));
                  }}
                  placeholder="Not set"
                  className="tabular h-11 w-24 rounded-md border border-control-border bg-chalk px-3 text-right text-[14px]"
                />
                <span className="w-4 text-[13px] text-ink-3">
                  {target.unit === 'share' ? '%' : ''}
                </span>
              </span>
            </label>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          What a director may set
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
          {/*
            ⚠ "TWENTY-EIGHT IS THE LARGEST TABLE PLAN" survived the abolition of the player cap on
            2026-09-05, and it is a different claim from the one that was abolished — this is a
            BOUND the commissioner sets, not a national rule, and its default happens to be 28.
            Reworded so it describes the field rather than asserting a platform limit.
          */}
          These are the walls a director builds a league inside, and they are yours to move. They
          start where the platform has been exercised rather than where it stops.
        </p>

        <div className="mt-4 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          <Bound
            label="Largest league"
            what="Nobody may set a player cap above this."
            value={bounds.maxPlayerCap}
            onChange={(value) => {
              setSaved(false);
              setBounds({ ...bounds, maxPlayerCap: value });
            }}
          />
          <Bound
            label="Smallest league"
            what="Below this there are not enough people for a table plan."
            value={bounds.minPlayerCap}
            onChange={(value) => {
              setSaved(false);
              setBounds({ ...bounds, minPlayerCap: value });
            }}
          />
          {/*
            ⚠ IT CAPS FOUNDING STATUS, NEVER DIRECTORS, and the wording has to carry that. Past the
            cap the founding claim falls through and the director joins on the standard plan — a
            full cohort is a normal state, not an error, and a label reading "largest cohort" would
            imply the platform stops accepting people.
          */}
          <Bound
            label="Founding places"
            what="How many directors get founding status and the founding rate. Past it they join on the standard plan; nobody is turned away."
            value={bounds.foundingDirectorCap}
            onChange={(value) => {
              setSaved(false);
              setBounds({ ...bounds, foundingDirectorCap: value });
            }}
          />
          <Bound
            label="Hours to pay after leaving the waitlist"
            what="A player promoted from the waitlist has this long to pay for their seat. After that the director can offer it to the next person."
            value={bounds.waitlistPaymentHours}
            onChange={(value) => {
              setSaved(false);
              setBounds({ ...bounds, waitlistPaymentHours: value });
            }}
          />
          <Bound
            label="Roster locks this many days out"
            what="After it, roster changes need an override and flag the print pack."
            value={bounds.rosterLockDays}
            onChange={(value) => {
              setSaved(false);
              setBounds({ ...bounds, rosterLockDays: value });
            }}
          />
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-3.5">
            <span className="min-w-0">
              <span className="block text-[14px] font-medium">League lengths</span>
              <span className="block text-[13px] text-ink-2">
                How many sessions a league may run for. Each director chooses.
              </span>
            </span>
            <span className="tabular text-[14px]">
              {LEAGUE_SESSIONS.min} to {LEAGUE_SESSIONS.max} sessions
            </span>
          </div>
        </div>
      </section>

      <div className="flex items-center gap-4">
        <Button disabled={save.isPending} onClick={submit}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {saved ? <span className="text-[13px] text-sage">Saved.</span> : null}
      </div>
    </div>
  );
}

function Bound({
  label,
  what,
  value,
  onChange,
}: {
  label: string;
  what: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <label className="flex flex-wrap items-center justify-between gap-4 px-5 py-3.5">
      <span className="min-w-0">
        <span className="block text-[14px] font-medium">{label}</span>
        <span className="block text-[13px] text-ink-2">{what}</span>
      </span>
      <input
        inputMode="numeric"
        value={value === null ? '' : String(value)}
        onChange={(event) => {
          const trimmed = event.target.value.trim();
          onChange(trimmed === '' ? null : Number(trimmed));
        }}
        className="tabular h-11 w-24 rounded-md border border-control-border bg-chalk px-3 text-right text-[14px]"
      />
    </label>
  );
}
